import { FileText, Link as LinkIcon, HardDrive, Wrench, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getPosts } from '../features/blog/api/postApi';
import { useAuthContext } from '../components/AuthProvider';
import { supabase } from '../lib/supabase';

type FeatureCardProps = {
  icon: React.ElementType;
  title: string;
  description: string;
  count?: number;
  to: string;
  loading?: boolean;
  disabled?: boolean;
};

function FeatureCard({ 
  icon: Icon, 
  title, 
  description, 
  count, 
  to, 
  loading,
  disabled 
}: FeatureCardProps) {
  const Component = disabled ? 'div' : Link;
  
  return (
    <Component 
      to={to}
      className={`bg-white rounded-lg shadow-sm p-6 ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md transition-shadow cursor-pointer'
      }`}
    >
      <div className="flex items-start space-x-4">
        <div className="p-3 bg-indigo-50 rounded-lg">
          <Icon className="w-6 h-6 text-indigo-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            {count !== undefined && (
              <span className={`text-sm px-2 py-1 rounded-full ${loading ? 'bg-gray-100' : 'bg-indigo-50'}`}>
                {loading ? '...' : count}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
      </div>
    </Component>
  );
}

export default function Home() {
  const { isAuthenticated } = useAuthContext();
  
  // 检查数据库连接状态
  const { data: dbStatus, isLoading: dbLoading } = useQuery({
    queryKey: ['dbStatus'],
    queryFn: async () => {
      try {
        // 尝试获取系统配置表的记录数
        const { data, error } = await supabase
          .from('system_config')
          .select('*')
          .eq('key', 'site_info')
          .single();
          
        console.log('数据库查询结果:', data); // 添加调试日志
          
        if (error) {
          console.error('数据库连接错误:', error);
          return { connected: false, error: error.message };
        }
        
        return { 
          connected: true, 
          data: {
            key: data.key,
            value: data.value,
            description: data.description
          }
        };
      } catch (error) {
        console.error('数据库连接错误:', error);
        return { connected: false, error: String(error) };
      }
    },
    retry: 1,
    retryDelay: 1000,
    staleTime: 30000, // 30秒内不重新获取
  });

  // 渲染数据库状态
  const renderDatabaseStatus = () => {
    if (dbLoading) {
      return <div className="text-gray-500">正在检查连接...</div>;
    }

    if (!dbStatus?.connected) {
      return (
        <div className="text-red-500">
          数据库连接失败: {dbStatus?.error || '未知错误'}
        </div>
      );
    }

    return <div className="text-green-500">数据库连接正常</div>;
  };

  // 获取博客文章
  const { data: posts, isLoading: postsLoading } = useQuery({
    queryKey: ['posts'],
    queryFn: getPosts,
    enabled: dbStatus?.connected, // 仅在数据库连接成功时获取数据
  });

  const features = [
    {
      icon: FileText,
      title: '博客管理',
      description: '管理和发布您的博客文章',
      count: posts?.length,
      to: '/blog',
      loading: postsLoading,
      disabled: !dbStatus?.connected
    },
    {
      icon: LinkIcon,
      title: '导航管理',
      description: '管理网站导航链接',
      to: '/navigation',
      disabled: !dbStatus?.connected
    },
    {
      icon: HardDrive,
      title: '云存储',
      description: '管理您的文件和媒体',
      to: '/cloud',
      disabled: !dbStatus?.connected
    },
    {
      icon: Wrench,
      title: '工具箱',
      description: '实用工具和功能',
      to: '/tools',
      disabled: !dbStatus?.connected
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* 数据库状态 */}
      <div className="mb-8 p-4 bg-white rounded-lg shadow-sm">
        <div className="flex items-center space-x-4">
          <div className={`p-2 rounded-full ${dbStatus?.connected ? 'bg-green-50' : 'bg-red-50'}`}>
            <Database className={`w-6 h-6 ${dbStatus?.connected ? 'text-green-600' : 'text-red-600'}`} />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              数据库状态
            </h3>
            {renderDatabaseStatus()}
            {dbStatus?.data && (
              <div className="mt-2 text-sm text-gray-500">
                <div>站点名称: {dbStatus.data.value?.name || '未设置'}</div>
                <div>版本: {dbStatus.data.value?.version || '未知'}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 未登录提示 */}
      {!isAuthenticated && (
        <div className="mb-8 p-4 bg-yellow-50 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                您尚未登录
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  部分功能可能受限。
                  <Link to="/auth/login" className="font-medium text-yellow-800 underline hover:text-yellow-900">
                    点击此处登录
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 功能卡片 */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {features.map((feature) => (
          <FeatureCard
            key={feature.title}
            {...feature}
            disabled={!isAuthenticated || feature.disabled}
          />
        ))}
      </div>
    </div>
  );
}